import { useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { authDebugInfo, authDebugWarn, redactValue } from '../../utils/authDebug';

/**
 * Maximum allowed length for query parameters to prevent DoS attacks
 * Same limit as AcceptInvitation component for consistency
 */
const MAX_PARAM_LENGTH = 500;

/**
 * Validates that a query parameter is safe to redirect
 *
 * **Security Checks:**
 * - Non-empty string validation
 * - Length limit enforcement (max 500 chars)
 * - XSS prevention (blocks script tags, javascript:, data:, vbscript: URIs)
 * - Type safety (must be string)
 *
 * @param value - The query parameter value to validate
 * @returns True if the value is safe to use
 *
 * @security OWASP A03:2021 - Injection Prevention
 */
const isValidParam = (value: string | null): boolean => {
  // Type check: must be string
  if (!value || typeof value !== 'string') return false;

  const trimmedValue = value.trim();

  // Length check: prevent DoS via extremely long params
  if (trimmedValue.length === 0 || trimmedValue.length > MAX_PARAM_LENGTH) return false;

  // XSS prevention: block common attack vectors
  // Regex checks for: <script, javascript:, data:, vbscript:
  if (/<script|javascript:|data:|vbscript:/i.test(trimmedValue)) return false;

  return true;
};

/**
 * InvitationHandler Component
 *
 * @component
 * Intercepts invitation parameters from the root URL and redirects to the proper route.
 *
 * **Security Features:**
 * - Input validation on all query parameters (OWASP A03:2021)
 * - XSS prevention through parameter sanitization
 * - DoS prevention via length limits
 * - Fixed redirect destination (no open redirect vulnerability)
 * - No sensitive data logging
 *
 * **Why this is needed:**
 * Auth0 generates invitation links that go to the root URL (/) with query params:
 * `/?invitation=TOKEN&organization=org_XXX&organization_name=NAME`
 *
 * This component validates and redirects to `/accept-invitation` for proper handling.
 *
 * **Flow:**
 * 1. User clicks Auth0-generated invitation link -> lands on /?invitation=...
 * 2. This component validates all parameters for security
 * 3. If valid, redirects to /accept-invitation with sanitized params
 * 4. If invalid, silently ignores (no redirect to prevent attack exploitation)
 * 5. AcceptInvitation component handles the Auth0 login flow
 *
 * @param children - Child components to render
 * @returns Children with invitation redirect logic
 *
 * @security Implements OWASP Top 10 secure coding practices
 */
export const InvitationHandler: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Only check on root path - prevent unnecessary processing
    if (location.pathname !== '/') {
      return;
    }

    const invitation = searchParams.get('invitation');
    const organization = searchParams.get('organization');
    const organizationName = searchParams.get('organization_name');

    // SECURITY: Validate all parameters before redirecting
    const isInvitationValid = isValidParam(invitation);
    const isOrganizationValid = isValidParam(organization);

    authDebugInfo('InvitationHandler inspected root query params', {
      pathname: location.pathname,
      invitation: redactValue(invitation),
      organization: redactValue(organization),
      organizationName: redactValue(organizationName),
      isInvitationValid,
      isOrganizationValid,
    });

    // Only proceed if required params are valid
    // organization_name is optional and validated separately if present
    if (isInvitationValid && isOrganizationValid) {
      // Create sanitized params - only add validated values
      const params = new URLSearchParams();
      params.set('invitation', invitation as string);
      params.set('organization', organization as string);

      // Optional param: only add if present and valid
      if (organizationName && isValidParam(organizationName)) {
        params.set('organization_name', organizationName);
      }

      authDebugInfo('InvitationHandler redirecting to /accept-invitation', {
        hasOrganizationName: Boolean(params.get('organization_name')),
      });

      // SECURITY: Fixed redirect path - no open redirect vulnerability
      // Always redirects to /accept-invitation (never to user-controlled destination)
      navigate(`/accept-invitation?${params.toString()}`, { replace: true });
      return;
    }

    if (invitation || organization || organizationName) {
      authDebugWarn('InvitationHandler found invalid invitation params on root route', {
        invitation: redactValue(invitation),
        organization: redactValue(organization),
        organizationName: redactValue(organizationName),
      });
    }
    // SECURITY: If params are invalid, silently ignore
    // No error messages that could reveal system information
    // No logging of potentially malicious parameter values
  }, [location.pathname, searchParams, navigate]);

  return <>{children}</>;
};
